<template>
  <v-dialog v-model="dialog" max-width="720">
    <v-card>
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t('strings.add-endpoint') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert v-if="!file" type="warning" variant="tonal" class="mb-4">
          {{ $t('strings.no-file-selected') }}
        </v-alert>

        <v-row dense>
          <v-col cols="12">
            <v-text-field
              :model-value="file?.file_name || ''"
              :label="$t('labels.file-name')"
              variant="outlined"
              density="compact"
              readonly
            ></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-radio-group v-model="mode" inline class="mb-2">
        <v-radio :label="$t('strings.add-to-new-datasource')" value="new"></v-radio>
          <v-radio
            :label="$t('strings.add-to-existing-datasource')"
            value="existing"
            :disabled="datasources.length === 0"
          ></v-radio>
        </v-radio-group>

        <v-row dense>
          <v-col cols="12" v-if="mode === 'new'">
            <v-text-field
              v-model="newDatasourceName"
              :label="$t('labels.datasource-name')"
              :hint="$t('strings.datasource-name-hint')"
              variant="outlined"
              density="compact"
              :rules="[requiredRule]"
              clearable
            ></v-text-field>

              <v-textarea
                v-model="newDatasourceDescription"
                :label="$t('labels.description')"
                :hint="$t('strings.datasource-project-description')"
                auto-grow
                density="compact"
                variant="outlined"
                :clearable="true"
                rows="3"
                row-height="20"
              ></v-textarea>
          </v-col>

          <v-col cols="12" v-else>
            <v-select
              v-model="selectedDatasourceId"
              :items="datasources"
              item-title="name"
              item-value="_id"
              :label="$t('labels.datasource')"
              variant="outlined"
              density="compact"
              :disabled="datasources.length === 0"
              clearable
            ></v-select>

            <v-alert v-if="datasources.length === 0" type="info" variant="tonal" class="mt-2">
              {{ $t('strings.no-datasources-available') }}
            </v-alert>
          </v-col>
        </v-row>

        <v-alert type="warning" variant="tonal" class="mt-4">
          {{ $t('strings.redirect-warning') }}
        </v-alert>

        <v-checkbox
          v-model="redirectToDatasources"
          :label="$t('strings.redirect-choice')"
          density="compact"
          class="mt-2"
          hide-details
        ></v-checkbox>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="close">
          {{ $t('labels.cancel') }}
        </v-btn>
        <v-btn color="secondary" variant="text" :disabled="!canSubmit" @click="submit">
          {{ $t('labels.add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  file: {
    type: Object,
    default: null,
  },
  dataset: {
    type: Object,
    default: null,
  },
  datasources: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const { t } = useI18n()

const dialog = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const mode = ref('existing')
const newDatasourceName = ref('')
const newDatasourceDescription = ref('')
const selectedDatasourceId = ref(null)
const redirectToDatasources = ref(false)

const requiredRule = (value) => !!value || t('strings.value-required')

watch(
  () => props.modelValue,
  (value) => {
    if (!value) return
    mode.value = props.datasources.length > 0 ? 'existing' : 'new'
    newDatasourceName.value = ''
    newDatasourceDescription.value = ''
    selectedDatasourceId.value = props.datasources?.[0]?._id || null
    redirectToDatasources.value = false
  },
)

watch(mode, (value) => {
  if (value === 'new') {
    selectedDatasourceId.value = null
  } else {
    newDatasourceName.value = ''
    newDatasourceDescription.value = ''
  }
})

const canSubmit = computed(() => {
  if (!props.file?.file_url) return false
  if (mode.value === 'new') return !!newDatasourceName.value.trim()
  return !!selectedDatasourceId.value
})

function close() {
  dialog.value = false
}

function submit() {
  if (!canSubmit.value) return
  emit('confirm', {
    mode: mode.value,
    datasourceName: mode.value === 'new' ? newDatasourceName.value.trim() : '',
    datasourceDescription: mode.value === 'new' ? newDatasourceDescription.value.trim() : '',
    datasourceId: mode.value === 'existing' ? selectedDatasourceId.value || '' : '',
    file: props.file,
    redirectToDatasources: redirectToDatasources.value,
  })
  close()
}
</script>
